// imports
import { forEach } from 'lodash';
import './styles/graphic.scss';
import * as d3 from 'd3';

// Parse Dates
const parseDate = d3.utcParse('%Y-%m-%d');

let svg;
let xAxis;
let yAxis;
let affordabilityLabel;
let margin;
let width;
let height;
let chartGroup;
let prevValProp;
let prevWidth;
let prevSelectedPlaces = new Set([]);
let selectedList = [];
let colorMap;

let metros;
let national;
let allPlaces;
let zeroLine;
let shadingRect;
let moreAffordable;
let moreAffordableArrow;
let lessAffordable;
let lessAffordableArrow;
let affordableArrows;
let affordableLabels;
let labelsGroup;
let lineGroup;
let startDate;
let endDate;
let metroKey;

// scales
let xScale;
let yScale;

let transitionTime = 0;
const defaultTransitionTime = 300;
const heightTotal = 500;


let jsonDataGlobal;
let simulation;

let selectedNamesCount = {};

function getMargins() {
  return window.innerWidth < 600
    ? {
      top: 10, right: 20, bottom: 30, left: 24, botSpace: true,
    }
    : {
      top: 20, right: 150, bottom: 20, left: 30, botSpace: true,
    };
}

function setDimensions(container) {
  margin = getMargins();
  width = container.node().getBoundingClientRect().width - margin.left - margin.right;
  height = heightTotal - margin.top - margin.bottom;
}

function resolveLabelCollisions(labels, threshold) {
  labels.sort((a, b) => a.labelY - b.labelY);
  const fixedIndex = labels.findIndex(l => l.name === 'United States');
  const fixedLabel = labels[fixedIndex];

  for (let i = fixedIndex + 1; i < labels.length; i++) {
    const prev = labels[i - 1];
    if (labels[i].labelY < prev.labelY + threshold) {
      labels[i].labelY = prev.labelY + threshold;
    }
  }

  for (let i = fixedIndex - 1; i >= 0; i--) {
    const next = labels[i + 1];
    if (labels[i].labelY > next.labelY - threshold) {
      labels[i].labelY = next.labelY - threshold;
    }
  }

  return labels;
}

function getKeyDisplayName(d) {
  const name = d.display || d.place;
  const isDup = selectedNamesCount[name] && selectedNamesCount[name] > 1;

  return (isDup) ? `${name}, ${d.state}` : name;
}

function setupChart(container) {
  setDimensions(container);
  svg = container.append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom);

  // Append the marker (arrowhead) to the SVG defs
  svg.append('defs')
    .append('marker')
    .attr('id', 'arrow_')
    .attr('viewBox', '0 0 10 10')
    .attr('refX', 9) // where arrow tip is placed
    .attr('refY', 5)
    .attr('markerWidth', 6)
    .attr('markerHeight', 6)
    .attr('orient', 'auto')
    .append('path')
    .attr('d', 'M 0 0 L 10 5 L 0 10 Z');

  svg.append('defs')
    .append('marker')
    .attr('id', 'arrow')
    .attr('viewBox', '0 0 10 10')
    .attr('refX', 5)
    .attr('refY', 5)
    .attr('markerWidth', 8)
    .attr('markerHeight', 8)
    .attr('orient', 'auto')
    .append('path')
    .attr('d', 'M2,2 L5,5 L2,8')
    .attr('stroke', '#A6A6A6')
    .attr('stroke-width', '1');

  chartGroup = svg.append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  xAxis = chartGroup.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${height})`);

  yAxis = chartGroup.append('g')
    .attr('class', 'y-axis');

  shadingRect = chartGroup.append('rect')
    .attr('class', 'shading')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', 0)
    .attr('fill', '#f0f0f0')
    .lower();

  zeroLine = chartGroup.append('line')
    .attr('class', 'zero-line')
    .attr('x1', 0)
    .attr('x2', width)
    .attr('opacity', '0')
    .lower();


  affordableLabels = svg.append('g');
  moreAffordable = affordableLabels
    .append('g');


  const moreAffordableTxt = moreAffordable
    .append('text')
    .attr('transform', 'rotate(0)')
    .attr('class', 'more-affordable')
    .attr('text-anchor', 'start')
    .attr('x', '1')
    .attr('y', '0')
    .attr('alignment-baseline', 'hanging')
    .text('More affordable');


  lessAffordable = affordableLabels
    .append('g');

  const textBottomDiff = margin.botSpace ? 10 : 0;
  const lessAffordableTxt = lessAffordable
    .append('text')
    .attr('transform', `translate(0, ${height - margin.bottom}`)
    .attr('class', 'less-affordable')
    .attr('text-anchor', 'start')
    .attr('x', '1')
    .attr('y', height + margin.top - textBottomDiff)
    .attr('alignment-baseline', 'baseline');


  lessAffordableTxt.append('tspan')
    .attr('dx', 0)
    .attr('dy', 0)
    .attr('text-anchor', 'start')
    .attr('alignment-baseline', 'baseline')
    .text('More expensive');


  affordableArrows = svg.append('g')
    .attr('class', 'affordable-arrows')
    .attr('transform', 'translate(150,0');

  const arrowX = margin.left;

  moreAffordableArrow = affordableArrows.append('line')
    .attr('x1', arrowX)
    .attr('y1', height / 2)
    .attr('x2', arrowX)
    .attr('y2', height / 2)
    .attr('stroke-width', 1)
    .attr('marker-end', 'url(#arrow)');

  lessAffordableArrow = affordableArrows.append('line')
    .attr('x1', arrowX)
    .attr('y1', height / 2)
    .attr('x2', arrowX)
    .attr('y2', height / 2)
    .attr('stroke-width', 1)
    .attr('marker-end', 'url(#arrow)');


  affordabilityLabel = svg.append('text')
    .attr('class', 'affordability-label')
    .attr('alignment-baseline', 'middle')
    .attr('dx', '1')
    .attr('dy', height / 2)
    .attr('opacity', '0');


  affordabilityLabel.append('tspan')
    .attr('x', 30)
    .attr('dy', -5)
    .attr('text-anchor', 'start')
    .text('Affordability');

  affordabilityLabel.append('tspan')
    .attr('x', 30)
    .attr('dy', 15)
    .attr('text-anchor', 'start')
    .text('threshold');

  lineGroup = chartGroup.append('g').attr('class', 'lines');

  labelsGroup = chartGroup.append('g').attr('class', 'labels');

  metroKey = d3.select('#controls .metros');
}

function updateChart(val_prop) {
  const dateRange = jsonDataGlobal.date_range;
  startDate = parseDate(dateRange[0]);
  endDate = parseDate(dateRange[1]);
  xScale = d3.scaleUtc().domain([startDate, endDate]).range([0, width]);
  yScale = d3.scaleLinear().range([height, 0]);


  const filteredData = ['United States', ...Array.from(prevSelectedPlaces)].map((place) => {
    const placeData = allPlaces.find(d => d.name === place);
    if (!placeData) return null;
    return {
      name: placeData.name,
      state: placeData.state,
      display: placeData.place,
      values: placeData.values.map(d => ({ date: parseDate(d.date), score: d.score === null ? null : +d.score })),
    };
  }).filter(Boolean);

  const allScores = filteredData.flatMap(d => d.values.map(v => v.score)).filter(v => v !== null);
  let ymin = Math.min(10, d3.min(allScores) - 25);
  if (ymin < 0) ymin = 0;
  let ymax = Math.max(d3.max(allScores) + 10, 150);
  if (filteredData.length < 2) { ymin = 0; ymax = 200; }
  yScale.domain([ymin, ymax]);

  filteredData.forEach((d) => {
    const lastValue = d.values.filter(v => v.score !== null).slice(-1)[0];
    d.labelY = lastValue ? yScale(lastValue.score) : yScale(100);
  });

  resolveLabelCollisions(filteredData, 14);

  const adjustedEndDate = d3.utcYear.offset(endDate, 1);
  const tickValues = d3.utcYear.every(1).range(startDate, adjustedEndDate).filter(d => d.getUTCFullYear() % 5 === 0);
  xAxis.transition().duration(transitionTime).call(d3.axisBottom(xScale).tickValues(tickValues).tickFormat(d3.utcFormat('%Y')).tickSizeOuter(0));

  // Subtle y-axis: fixed labels 50, 100, 150 only; no domain/tick lines
  const yTicks = [50, 100, 150]
    .filter(v => v >= yScale.domain()[0] && v <= yScale.domain()[1]);
  yAxis.transition().duration(transitionTime)
    .call(d3.axisLeft(yScale)
      .tickValues(yTicks)
      .tickFormat(d3.format('d'))
      .tickSize(0)
      .tickSizeOuter(0));
  yAxis.select('.domain').remove();

  const line = d3.line()
    .x(d => xScale(d.date))
    .y(d => yScale(d.score))
    .defined(d => d.score !== null)
    .curve(d3.curveMonotoneX);

  zeroLine
    .transition()
    .duration(transitionTime)
    .attr('opacity', 1)
    .attr('y1', yScale(100))
    .attr('y2', yScale(100));

  shadingRect.transition().duration(transitionTime).attr('y', yScale(100)).attr('height', height - yScale(100));
  // affordableLabels.transition().duration(transitionTime).attr('transform', `translate(10,${yScale(100)})`);
  affordableLabels.attr('transform', 'translate(0,0)');
  // lessAffordable.attr('transform', `translate(0, ${yScale(0) + margin.top})`);


  // affordableArrows
  //   .transition().duration(transitionTime).attr('opacity', '1');

  moreAffordableArrow
    .transition()
    .duration(transitionTime)
    .attr('y1', yScale(100) - 10)
    .attr('y2', 20)
    .attr('opacity', '1');

  const arrowDist = margin.botSpace ? 30 : 20;
  lessAffordableArrow
    .transition()
    .duration(transitionTime)
    .attr('y1', yScale(100) + margin.top + 25)
    .attr('y2', margin.top + height - arrowDist)
    .attr('opacity', '1');

  affordabilityLabel
    .transition().duration(transitionTime)
    .attr('transform', `translate(0, ${yScale(100) + margin.top})`)
    .attr('opacity', '1');

  const update = lineGroup.selectAll('.line-group').data(filteredData, d => d.name);
  const enterGroups = update.enter().append('g').attr('class', 'line-group').attr('data-name', d => d.name)
    .style('--line-color', d => colorMap.get(d.name) || '#999999');
  const allGroups = enterGroups.merge(update);

  allGroups.each(function (d) {
    const pathGroup = d3.select(this);
    const path = pathGroup.selectAll('.line-segment').data([d.values]);
    const pathEnter = path.enter().append('path')
      .attr('class', d.name === 'United States' ? 'line-segment national' : 'line-segment metro')
      .attr('d', line(d.values))
      .style('opacity', 0)
      .attr('data-display', d => d.display);

    pathEnter.transition().duration(transitionTime).style('opacity', 1);
    pathEnter.merge(path).transition().duration(transitionTime).style('opacity', 1)
      .attr('d', line);
  });

  update.exit().remove();

  // The labels next to the lines
  const labels = labelsGroup.selectAll('.line-label').data(filteredData, d => d.name);
  const enterLabels = labels.enter().append('text')
    .attr('x', 3)
    .attr('dy', '.35em')
    .style('font', 'CNN Sans Display 12px')
    .attr('class', d => (d.name === 'United States' ? 'line-label national' : 'line-label metro'))
    .style('--line-color', d => colorMap.get(d.name) || '#999999')
    .attr('dx', d => (window.innerWidth < 600 ? -20 : 0))
    .attr('dy', d => (window.innerWidth < 600 ? 8 : 0))
    .attr('transform', ((d) => {
      const lastValueArray = d.values.filter(v => v.score !== null).slice(-1);
      const score = lastValueArray.length > 0 ? lastValueArray[0].score : 100;
      return `translate(${xScale(endDate)},${yScale(score)})`;
    }))
    .text(d => (d.name === 'United States' ? 'National average' : getKeyDisplayName(d)));

  enterLabels.merge(labels).transition().duration(transitionTime)
    .attr('transform', d => `translate(${xScale(endDate)},${d.labelY})`)
    .text(d => (d.name === 'United States' ? 'National average' : getKeyDisplayName(d)));

  labels.exit().remove();


  // The labels for the key under the search box (what you've searched for)
  const listItems = metroKey.selectAll('li')
    .data(selectedList, d => d.name);

  const enterItems = listItems.enter()
    .append('li')
    .text(d => d.place)
    .style('--line-color', d => colorMap.get(d.name) || '#999999')
    .on('click', (event, d) => {
      const customEvent = new CustomEvent('update-selected-places', {
        detail: {
          removePlace: d.name,
        },
      });

      document.querySelector('#graphic').dispatchEvent(customEvent);
    });

  // If you want to update content (optional, depending on your needs)
  listItems.merge(enterItems)
    .text(d => getKeyDisplayName(d));

  // Remove items no longer in the data
  listItems.exit().remove();
}

// Replace optional chaining in initDodgePlot function
export default function initDodgePlot(config, container, isMobile, jsonData, val_prop = 'last_score', selectedPlaces = new Set([]), placeToColor = new Map()) {
  const initialWidth = config && config.initialWidth ? config.initialWidth : null;
  const shouldRedraw = initialWidth && initialWidth !== prevWidth;
  const shouldUpdate = selectedPlaces.size !== prevSelectedPlaces.size;

  jsonDataGlobal = jsonData;
  prevSelectedPlaces = new Set([...selectedPlaces]);

  selectedList = Array.from(selectedPlaces).map(name => jsonData.data.metros.find(d => d.name === name)).filter(Boolean);
  colorMap = placeToColor;


  selectedNamesCount = selectedList.reduce(
    (acc, cur) => {
      if (acc[cur.place]) {
        acc[cur.place] = acc[cur.place] + 1;
      } else {
        acc[cur.place] = 1;
      }
      return acc;
    },
    {},
  );


  if (!svg || shouldRedraw) {
    container.selectAll('svg').remove();
    svg = null;
    metros = jsonData.data.metros;
    national = jsonData.data.national;
    allPlaces = [...national, ...metros];
    const dateRange = jsonData.date_range;
    startDate = parseDate(dateRange[0]);
    endDate = parseDate(dateRange[1]);
    xScale = d3.scaleUtc().domain([startDate, endDate]).range([0, width]);
    yScale = d3.scaleLinear().range([height, 0]);
    setupChart(container);
    updateChart(val_prop);
  } else if (shouldUpdate) {
    transitionTime = defaultTransitionTime;
    updateChart(val_prop);
  }

  if (config) prevWidth = initialWidth;
  if (val_prop) prevValProp = val_prop;
  if (window.pymChild) window.pymChild.sendHeight();
}
